import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Button, Tooltip } from '@material-ui/core';
import { HtmlView, Panel, BackButton, EditablePanel } from '../../../../../../Components';
import { ICompliance, ComplianceUsers } from '../../../../Components/Compliance/ComplianceUsers';
import { IRequirementChange, LatestChange } from '../Changes';
import { RequirementNotes } from './RequirementNotes';
import { IGridQueryRequest, INamedEntity } from '../../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../../Api';
import { useStyles } from "../../../../LawPortal.styles";
import { ArrowUpward, AssignmentTurnedIn } from '@material-ui/icons';
import { useIntl } from "react-intl";
import { EditChecklistWizard } from './EditChecklistWizard';
import { RequirementAddChange, IRequirementChange as AdminIRequirementChange } from '../../../../../Administration/Features/LawPortal/OtherRequirements/Changes/Changes.Form';
import { RequirementChangesView } from '../../../../../Administration/Features/LawPortal/OtherRequirements/Changes/Changes.View';
import { AccessLevel, LawPortalFeature, UserContext } from '../../../../../../Context/UserContext/UserContext';

export interface ICompanyUnitRequirement extends INamedEntity {
  hasNextComplianceInterval: boolean;
  note: string | null;
}

interface IState {
  shortDescription: string;
  latestChange?: IRequirementChange;
  companyUnits: Array<ICompanyUnitRequirement>;
  internalComments: Array<{ from: string; comment: string; createdAt: Date }>;
}

interface IRequirement extends INamedEntity {
  name: string;
  area: INamedEntity;
  effectiveFrom: Date;
  effectiveTo: Date | null;
  effectiveToSendoutTime: Date | null;
  description: string;
  changes: IRequirementChange[];
}

export const RequirementOverview = () => {
  const params = useParams<{ id: string }>();
  const id = parseInt(params.id!);
  const { api, abortController } = useAptorApi();
  const [overview, setOverview] = useState<IState>();
  const classes = useStyles();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [initState, setInitState] = useState<IRequirement>();
  const [revision, setRevision] = useState<number>(0);
  const [refetch, setRefetch] = useState(false);
  const { hasAccessToFeature } = useContext(UserContext);
  const canManage = hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage);
  const accessToOtherRequirements = hasAccessToFeature(LawPortalFeature.OtherRequirements, AccessLevel.Read);

  useEffect(() => {
    getOurReqData();
    getRequirementDetails();
  }, [api, abortController, id]);

  const getOurReqData = () => {
    api.getLawPortalRequirementOverview<IState>(id).then((overview) => {
      if (abortController.current.signal.aborted) {
        return;
      }
      setOverview(overview);
    });
  };

  const getRequirementDetails = () => {
    api.getRequirement<IRequirement>(id).then((requirement) => {
      if (abortController.current.signal.aborted) {
        return;
      }
      setInitState(requirement);
    });
  };
  // useEffect(() => {
  //   api.getLawPortalRequirementOverview<IState>(id).then((overview) => {
  //     if (abortController.current.signal.aborted) {
  //       return;
  //     }
  //     setOverview(overview);
  //   });
  // }, [api, abortController, id]);

  const updateState = useCallback(
    (state: Partial<IState>) => overview && setOverview({ ...overview, ...state }),
    [overview],
  );

  const dataFetcher = useCallback(
    (request: IGridQueryRequest) => api.searchRequirementComplianceUsers<ICompliance>(id, request),
    [id, api],
  );

  useEffect(() => {
    const initializeFields = async () => {
      const info = await api.getRequirement<IRequirement>(id);
      if (abortController.current.signal.aborted) {
        return;
      }
      setInitState(info);
      setRefetch(false);
    };
    if (refetch || initState === undefined) {
      initializeFields();
    }
  }, [refetch, id, api, abortController, initState]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const updateRequirementState = (update: Partial<IRequirement>) => {
    if (initState) {
      setInitState({ ...initState, ...update });
      setTimeout(() => setRevision(revision + 1), 250);
    }
  };

  if (overview === undefined) {
    return <></>;
  }

  return (
    <>
      <Grid item md={9} xs={12} style={{ display: 'flex', alignItems: 'center',justifyContent:'space-between'}}>
        {/* <BackButton disableMargin link="/law-portal/other-requirements" /> */}
        <BackButton disableMargin link="" onclick={() => navigate(-1)} />
        {/* <Button
          variant="outlined"
          color="primary"
          onClick={() => setWizardOpen(true)}
          style={{ marginLeft: '8px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <AssignmentTurnedIn />
            {formatMessage({ id: 'law-portal.manage.edit-law.lawChecklist' })}
          </div>
        </Button> */}
      </Grid>
      <EditChecklistWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        reqId={id}
        onComplete={() => getOurReqData()}
      />
      <Grid item md={9} xs={12}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <Panel titleKey="law-portal.requirements.overview.short-description">
              <HtmlView value={overview.shortDescription} />
            </Panel>
          </Grid>
          {/* <Grid item xs={12}>
            <RequirementComplianceInterval reqId={id} companyUnits={overview.companyUnits} />
          </Grid> */}
          {canManage && accessToOtherRequirements ? (
            <Grid item xs={12}>
              <EditablePanel
                iconType="add"
                titleKey="law-portal.manage.requirements.latest-change"
                dependencies={[revision]}
                view={
                  <RequirementChangesView
                    id={id}
                    changes={(initState?.changes || []) as any}
                    onChange={(params: any) => updateRequirementState({ changes: params.changes })}
                  />
                }
                form={<RequirementAddChange id={id} changes={initState?.changes as any} onChange={(params: any) => updateRequirementState({ changes: params.changes })} />}
              />
            </Grid>
          ):(
            overview.latestChange && (
            <Grid item xs={12}>
              <LatestChange reqId={id} change={overview.latestChange} onChange={updateState} />
            </Grid>
          )
          )}


          <Grid item xs={12}>
            <RequirementNotes reqId={id} companyUnits={overview.companyUnits} onChange={updateState} />
          </Grid>
          <Grid item xs={12}>
            <ComplianceUsers dataFetcher={dataFetcher} />
          </Grid>
        </Grid>
      </Grid>
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </>
  );
};